import { useState, useRef, useEffect, useLayoutEffect, ReactNode, KeyboardEvent, CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../Icon.js';

// ── ActionDropdown ────────────────────────────────────────────────────────────
// A pill-trigger dropdown for action menus (not value selection).
// Items can be actions (with onClick) or separators.

export type ActionMenuItem =
  | { type: 'action'; label: string; onClick: () => void; disabled?: boolean }
  | { type: 'separator' };

export interface ActionDropdownProps {
  label: string;
  items: ActionMenuItem[];
  size?: 'xs' | 'sm' | 'md';
  tone?: 'muted' | 'cyan' | 'warn';
}

// Visual tone applied to the trigger border + label color, so this dropdown
// matches the surrounding Pill outline aesthetic. Defaults to 'muted' which
// pairs with Pill tone='muted' outline.
const ACTION_TONE: Record<NonNullable<ActionDropdownProps['tone']>, { border: string; text: string }> = {
  muted: { border: 'var(--evo-fg-3)', text: 'var(--evo-fg-3)' },
  cyan:  { border: 'var(--evo-cyan)', text: 'var(--evo-cyan)' },
  warn:  { border: 'var(--evo-amber)', text: 'var(--evo-amber)' },
};

export function ActionDropdown({ label, items, size = 'sm', tone = 'muted' }: ActionDropdownProps) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<MenuPos | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const toneStyle = ACTION_TONE[tone];

  function computePos(): MenuPos | null {
    if (!triggerRef.current) return null;
    const rect = triggerRef.current.getBoundingClientRect();
    return { top: rect.bottom + 6, right: window.innerWidth - rect.right, minWidth: 180 };
  }

  useLayoutEffect(() => {
    if (!open) { setMenuPos(null); return; }
    setMenuPos(computePos());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      const t = e.target as Node;
      if (!triggerRef.current?.contains(t) && !menuRef.current?.contains(t)) setOpen(false);
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  const h = size === 'xs' ? '22px' : size === 'sm' ? '26px' : '32px';
  const fs = size === 'xs' ? '11px' : size === 'sm' ? '12px' : '13px';

  const menu = open && menuPos ? createPortal(
    <div
      ref={menuRef}
      style={{
        position: 'fixed',
        top: menuPos.top,
        right: menuPos.right,
        minWidth: menuPos.minWidth,
        background: 'var(--evo-bg-3)',
        border: '1px solid var(--evo-stroke)',
        borderRadius: 'var(--evo-r-md)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.32)',
        zIndex: 9999,
        overflow: 'hidden',
        padding: '4px',
        animation: 'fade-in 100ms ease-out',
      }}
    >
      {items.map((item, i) =>
        item.type === 'separator' ? (
          <div key={i} style={{ height: '1px', background: 'var(--evo-stroke)', margin: '4px 0' }} />
        ) : (
          <button
            key={i}
            disabled={item.disabled}
            onMouseDown={e => { e.preventDefault(); if (!item.disabled) { item.onClick(); setOpen(false); } }}
            style={{
              display: 'block', width: '100%', textAlign: 'left',
              padding: '8px 12px', borderRadius: '4px',
              fontSize: '13px', fontFamily: 'var(--evo-font)',
              color: item.disabled ? 'var(--evo-fg-3)' : 'var(--evo-fg-1)',
              background: 'transparent', border: 'none',
              cursor: item.disabled ? 'not-allowed' : 'pointer',
              opacity: item.disabled ? 0.5 : 1,
            }}
            onMouseEnter={e => { if (!item.disabled) (e.currentTarget as HTMLButtonElement).style.background = 'var(--evo-bg-2)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
          >
            {item.label}
          </button>
        )
      )}
    </div>,
    document.body,
  ) : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(v => !v)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '4px',
          height: h, padding: '0 12px', borderRadius: '9999px',
          border: `1px solid ${open ? 'var(--evo-cyan)' : toneStyle.border}`,
          background: open ? 'var(--evo-bg-3)' : 'transparent',
          color: open ? 'var(--evo-cyan)' : toneStyle.text,
          fontFamily: 'var(--evo-font)', fontSize: fs,
          fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap',
          transition: 'border-color 120ms linear, color 120ms linear',
        }}
      >
        {label}
        <span style={{ display: 'inline-flex', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
          <Icon name="chevron-down" size={10} strokeWidth={2} />
        </span>
      </button>
      {menu}
    </>
  );
}

// ── MultiActionDropdown ───────────────────────────────────────────────────────
// Pill-trigger dropdown that supports both checkbox items (queued for batch
// execution via a footer "Proceed" button) and direct-action items (click-once
// behavior, same as ActionDropdown). Use this when the user might want to
// chain multiple operations on the same target without dismissing the menu
// between each.
//
// Item types:
//   - { type: 'checkbox', key, label, disabled? } — queued. When checked,
//     'Proceed' is enabled. On click, fires onProceed(selectedKeys).
//   - { type: 'action', label, onClick, disabled? } — fires immediately.
//   - { type: 'separator' }

export type MultiActionItem =
  | { type: 'checkbox'; key: string; label: string; disabled?: boolean }
  | { type: 'action'; label: string; onClick: () => void; disabled?: boolean; title?: string }
  | { type: 'separator' };

export interface MultiActionDropdownProps {
  label: string;
  items: MultiActionItem[];
  onProceed: (selectedKeys: string[]) => void;
  proceedLabel?: string;
  proceedLoading?: boolean;
  size?: 'xs' | 'sm' | 'md';
  tone?: 'muted' | 'cyan' | 'warn';
}

export function MultiActionDropdown({
  label, items, onProceed, proceedLabel = 'Proceed',
  proceedLoading = false, size = 'sm', tone = 'muted',
}: MultiActionDropdownProps) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<MenuPos | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const toneStyle = ACTION_TONE[tone];

  function computePos(): MenuPos | null {
    if (!triggerRef.current) return null;
    const rect = triggerRef.current.getBoundingClientRect();
    return { top: rect.bottom + 6, right: window.innerWidth - rect.right, minWidth: 220 };
  }

  useLayoutEffect(() => {
    if (!open) { setMenuPos(null); return; }
    setMenuPos(computePos());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      const t = e.target as Node;
      if (!triggerRef.current?.contains(t) && !menuRef.current?.contains(t)) setOpen(false);
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  function toggle(key: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  function handleProceed() {
    if (selected.size === 0 || proceedLoading) return;
    const keys = Array.from(selected);
    onProceed(keys);
    setSelected(new Set());
    setOpen(false);
  }

  const h = size === 'xs' ? '22px' : size === 'sm' ? '26px' : '32px';
  const fs = size === 'xs' ? '11px' : size === 'sm' ? '12px' : '13px';
  const hasChecks = items.some(it => it.type === 'checkbox');

  const menu = open && menuPos ? createPortal(
    <div
      ref={menuRef}
      style={{
        position: 'fixed',
        top: menuPos.top,
        right: menuPos.right,
        minWidth: menuPos.minWidth,
        background: 'var(--evo-bg-3)',
        border: '1px solid var(--evo-stroke)',
        borderRadius: 'var(--evo-r-md)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.32)',
        zIndex: 9999,
        overflow: 'hidden',
        animation: 'fade-in 100ms ease-out',
        display: 'flex', flexDirection: 'column',
      }}
    >
      <div style={{ padding: '4px' }}>
        {items.map((item, i) => {
          if (item.type === 'separator') {
            return <div key={i} style={{ height: '1px', background: 'var(--evo-stroke)', margin: '4px 0' }} />;
          }
          if (item.type === 'action') {
            return (
              <button
                key={i}
                disabled={item.disabled}
                title={item.title}
                onMouseDown={e => { e.preventDefault(); if (!item.disabled) { item.onClick(); setOpen(false); } }}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '8px 12px', borderRadius: '4px',
                  fontSize: '13px', fontFamily: 'var(--evo-font)',
                  color: item.disabled ? 'var(--evo-fg-3)' : 'var(--evo-fg-1)',
                  background: 'transparent', border: 'none',
                  cursor: item.disabled ? 'not-allowed' : 'pointer',
                  opacity: item.disabled ? 0.5 : 1,
                }}
                onMouseEnter={e => { if (!item.disabled) (e.currentTarget as HTMLButtonElement).style.background = 'var(--evo-bg-2)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
              >
                {item.label}
              </button>
            );
          }
          // checkbox
          const isChecked = selected.has(item.key);
          return (
            <button
              key={item.key}
              type="button"
              disabled={item.disabled}
              onMouseDown={e => { e.preventDefault(); if (!item.disabled) toggle(item.key); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                width: '100%', textAlign: 'left',
                padding: '8px 12px', borderRadius: '4px',
                fontSize: '13px', fontFamily: 'var(--evo-font)',
                color: item.disabled ? 'var(--evo-fg-3)' : 'var(--evo-fg-1)',
                background: 'transparent', border: 'none',
                cursor: item.disabled ? 'not-allowed' : 'pointer',
                opacity: item.disabled ? 0.5 : 1,
              }}
              onMouseEnter={e => { if (!item.disabled) (e.currentTarget as HTMLButtonElement).style.background = 'var(--evo-bg-2)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
            >
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: '14px', height: '14px', flexShrink: 0, borderRadius: '3px',
                border: `1px solid ${isChecked ? 'var(--evo-cyan)' : 'var(--evo-fg-3)'}`,
                background: isChecked ? 'var(--evo-cyan)' : 'transparent',
                color: 'var(--evo-bg)',
              }}>
                {isChecked && <Icon name="ok" size={10} strokeWidth={3} />}
              </span>
              <span style={{ flex: 1 }}>{item.label}</span>
            </button>
          );
        })}
      </div>
      {hasChecks && (
        <div style={{
          borderTop: '1px solid var(--evo-stroke)',
          padding: '8px',
          background: 'var(--evo-bg-2)',
        }}>
          <button
            type="button"
            disabled={selected.size === 0 || proceedLoading}
            onMouseDown={e => { e.preventDefault(); handleProceed(); }}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: '100%', height: '28px', padding: '0 12px',
              borderRadius: '6px',
              background: selected.size === 0 ? 'var(--evo-bg-3)' : 'var(--evo-cyan)',
              border: `1px solid ${selected.size === 0 ? 'var(--evo-stroke)' : 'var(--evo-cyan)'}`,
              color: selected.size === 0 ? 'var(--evo-fg-3)' : 'var(--evo-bg)',
              fontFamily: 'var(--evo-font)', fontSize: '12px', fontWeight: 600,
              cursor: selected.size === 0 || proceedLoading ? 'not-allowed' : 'pointer',
              opacity: proceedLoading ? 0.6 : 1,
              transition: 'background 120ms linear, color 120ms linear',
            }}
          >
            {proceedLoading ? '…' : `${proceedLabel}${selected.size > 0 ? ` (${selected.size})` : ''}`}
          </button>
        </div>
      )}
    </div>,
    document.body,
  ) : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(v => !v)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '4px',
          height: h, padding: '0 12px', borderRadius: '9999px',
          border: `1px solid ${open ? 'var(--evo-cyan)' : toneStyle.border}`,
          background: open ? 'var(--evo-bg-3)' : 'transparent',
          color: open ? 'var(--evo-cyan)' : toneStyle.text,
          fontFamily: 'var(--evo-font)', fontSize: fs,
          fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap',
          transition: 'border-color 120ms linear, color 120ms linear',
        }}
      >
        {label}
        <span style={{ display: 'inline-flex', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
          <Icon name="chevron-down" size={10} strokeWidth={2} />
        </span>
      </button>
      {menu}
    </>
  );
}

export interface DropdownOption<T> {
  value: T;
  label: string;
  icon?: ReactNode;
  count?: number;
}

type SingleProps<T> = { multi?: false; value: T | null; onChange: (value: T) => void };
type MultiProps<T> = { multi: true; value: T[]; onChange: (values: T[]) => void };

export type DropdownProps<T> = (SingleProps<T> | MultiProps<T>) & {
  options: DropdownOption<T>[];
  placeholder?: string;
  size?: 'xs' | 'sm' | 'md';
  disabled?: boolean;
  align?: 'left' | 'right';
  searchable?: boolean;
  triggerStyle?: CSSProperties;
};

type MenuPos = { top: number; left?: number; right?: number; minWidth: number };

export function Dropdown<T extends string | number>({
  options,
  placeholder = 'Select…',
  size = 'md',
  disabled = false,
  align = 'left',
  searchable = false,
  triggerStyle,
  ...rest
}: DropdownProps<T>) {
  const [open, setOpen] = useState(false);
  const [focusedIdx, setFocusedIdx] = useState(-1);
  const [query, setQuery] = useState('');
  const [menuPos, setMenuPos] = useState<MenuPos | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const isMulti = rest.multi === true;

  function isSelected(opt: DropdownOption<T>): boolean {
    if (isMulti) return (rest.value).includes(opt.value);
    return rest.value === opt.value;
  }

  function handleSelect(opt: DropdownOption<T>) {
    if (isMulti) {
      const current = rest.value;
      const next = current.includes(opt.value)
        ? current.filter((v) => v !== opt.value)
        : [...current, opt.value];
      (rest as MultiProps<T>).onChange(next);
    } else {
      (rest as SingleProps<T>).onChange(opt.value);
      setOpen(false);
    }
  }

  // ── Position calculation ──────────────────────────────────────────────────

  function computePos(): MenuPos | null {
    if (!triggerRef.current) return null;
    const rect = triggerRef.current.getBoundingClientRect();
    if (align === 'right') {
      return { top: rect.bottom + 4, right: window.innerWidth - rect.right, minWidth: rect.width };
    }
    return { top: rect.bottom + 4, left: rect.left, minWidth: rect.width };
  }

  useLayoutEffect(() => {
    if (!open) { setMenuPos(null); return; }
    setMenuPos(computePos());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, align]);

  // Reposition on scroll / resize while open
  useEffect(() => {
    if (!open) return;
    function reposition() { setMenuPos(computePos()); }
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, align]);

  // ── Click outside ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      const target = e.target as Node;
      if (!containerRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  // ── Search focus ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (open && searchable) setTimeout(() => searchRef.current?.focus(), 0);
    if (!open) { setQuery(''); setFocusedIdx(-1); }
  }, [open, searchable]);

  const filtered = searchable && query
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  // ── Keyboard navigation ───────────────────────────────────────────────────

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'Escape') { e.preventDefault(); setOpen(false); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setFocusedIdx((i) => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setFocusedIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && focusedIdx >= 0) { e.preventDefault(); if (filtered[focusedIdx]) handleSelect(filtered[focusedIdx]); }
  }

  // ── Trigger label ─────────────────────────────────────────────────────────

  const triggerLabel = (() => {
    if (isMulti) {
      const sel = rest.value;
      if (sel.length === 0) return placeholder;
      if (sel.length === 1) return options.find((o) => o.value === sel[0])?.label ?? placeholder;
      return `${sel.length} selected`;
    }
    return options.find((o) => o.value === rest.value)?.label ?? placeholder;
  })();

  const isPlaceholder = isMulti ? (rest.value).length === 0 : rest.value === null;
  const h = size === 'xs' ? '18px' : size === 'sm' ? '28px' : '32px';
  const fs = size === 'xs' ? '10px' : size === 'sm' ? '12px' : '13px';
  const px = size === 'xs' ? '6px' : '10px';

  // ── Render ────────────────────────────────────────────────────────────────

  const menu = open && menuPos ? createPortal(
    <div
      ref={menuRef}
      style={{
        position: 'fixed',
        top: menuPos.top,
        ...(menuPos.left !== undefined ? { left: menuPos.left } : {}),
        ...(menuPos.right !== undefined ? { right: menuPos.right } : {}),
        minWidth: menuPos.minWidth,
        width: 'max-content',
        maxWidth: '320px',
        background: 'var(--evo-bg-3)',
        border: '1px solid var(--evo-stroke)',
        borderRadius: '6px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
        zIndex: 9999,
        overflow: 'hidden',
        animation: 'fade-in 100ms ease-out',
      }}
    >
      {searchable && (
        <div style={{ padding: '8px 8px 4px' }}>
          <input
            ref={searchRef}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setFocusedIdx(-1); }}
            placeholder="Search…"
            style={{
              width: '100%', height: '28px', padding: '0 8px',
              background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)',
              borderRadius: '4px', color: 'var(--evo-fg-2)', outline: 'none',
              fontFamily: 'var(--evo-font)', fontSize: '12px',
            }}
          />
        </div>
      )}
      <div style={{ maxHeight: '220px', overflowY: 'auto', padding: '4px' }}>
        {filtered.length === 0 && (
          <div style={{ padding: '8px 12px', fontSize: '12px', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)' }}>
            No results
          </div>
        )}
        {filtered.map((opt, i) => {
          const sel = isSelected(opt);
          return (
            <div
              key={String(opt.value)}
              onMouseEnter={() => setFocusedIdx(i)}
              onMouseDown={(e) => { e.preventDefault(); handleSelect(opt); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '6px 10px', borderRadius: '4px', cursor: 'pointer',
                whiteSpace: 'nowrap',
                background: i === focusedIdx ? 'var(--evo-cyan-12)' : 'transparent',
                color: sel ? 'var(--evo-cyan)' : 'var(--evo-fg-2)',
                fontFamily: 'var(--evo-font)', fontSize: '13px',
                transition: 'background 80ms',
              }}
            >
              {opt.icon && <span style={{ flexShrink: 0 }}>{opt.icon}</span>}
              <span style={{ flex: 1 }}>{opt.label}</span>
              {opt.count !== undefined && (
                <span style={{ fontSize: '11px', color: sel ? 'var(--evo-cyan)' : 'var(--evo-fg-3)', flexShrink: 0 }}>
                  {opt.count}
                </span>
              )}
              {sel && <span style={{ color: 'var(--evo-cyan)', flexShrink: 0, display: 'flex' }}><Icon name="ok" size={12} /></span>}
            </div>
          );
        })}
      </div>
    </div>,
    document.body,
  ) : null;

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}
      onKeyDown={handleKeyDown}
      tabIndex={disabled ? -1 : 0}
    >
      {/* Trigger */}
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          height: h, padding: `0 ${px}`,
          background: 'var(--evo-panel)',
          border: `1px solid ${open ? 'var(--evo-cyan)' : 'var(--evo-stroke)'}`,
          borderRadius: '4px',
          color: isPlaceholder ? 'var(--evo-fg-3)' : 'var(--evo-fg-2)',
          fontFamily: 'var(--evo-font)', fontSize: fs,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          whiteSpace: 'nowrap',
          transition: 'border-color 120ms linear',
          ...triggerStyle,
        }}
      >
        <span style={{ flex: 1, textAlign: 'left' }}>{triggerLabel}</span>
        <span style={{
          color: 'inherit',
          transform: open ? 'rotate(180deg)' : 'none',
          transition: 'transform 120ms linear',
          flexShrink: 0, display: 'flex', opacity: 0.6,
        }}>
          <Icon name="chevron-down" size={size === 'xs' ? 10 : 12} />
        </span>
      </button>

      {menu}
    </div>
  );
}
